"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var util_1 = require("@firebase/util");
var util_2 = require("../util/util");
var snap_1 = require("./snap");
var __childrenNodeConstructor;
/**
 * LeafNode is a class for storing leaf nodes in a DataSnapshot. It
 * implements Node and stores the value of the node (a string,
 * number, or boolean) accessible via getValue().
 */
var LeafNode = /** @class */ (function () {
 /**
 * @implements {Node}
 * @param {!(string|number|boolean|Object)} value_ The value to store in this leaf node.
 * The object type is possible in the event of a deferred value
 * @param {!Node=} priorityNode_ The priority of this node.
 */
 function LeafNode(value_, priorityNode_) {
 if (priorityNode_ === void 0) { priorityNode_ = LeafNode.__childrenNodeConstructor.EMPTY_NODE; }
 this.value_ = value_;
 this.priorityNode_ = priorityNode_;
 this.lazyHash_ = null;
 util_1.assert(this.value_ !== undefined && this.value_ !== null, "LeafNode shouldn't be created with null/undefined value.");
 snap_1.validatePriorityNode(this.priorityNode_);
 }
 Object.defineProperty(LeafNode, "__childrenNodeConstructor", {
 get: function () {
 return __childrenNodeConstructor;
 },
 set: function (val) {
 __childrenNodeConstructor = val;
 },
 enumerable: true,
 configurable: true
 });
 /** @inheritDoc */
 LeafNode.prototype.isLeafNode = function () {
 return true;
 };
 /** @inheritDoc */
 LeafNode.prototype.getPriority = function () {
 return this.priorityNode_;
 };
 /** @inheritDoc */
 LeafNode.prototype.updatePriority = function (newPriorityNode) {
 return new LeafNode(this.value_, newPriorityNode);
 };
 /** @inheritDoc */
 LeafNode.prototype.getImmediateChild = function (childName) {
 // Hack to treat priority as a regular child
 if (childName === '.priority') {
 return this.priorityNode_;
 }
 else {
 return LeafNode.__childrenNodeConstructor.EMPTY_NODE;
 }
 };
 /** @inheritDoc */
 LeafNode.prototype.getChild = function (path) {
 if (path.isEmpty()) {
 return this;
 }
 else if (path.getFront() === '.priority') {
 return this.priorityNode_;
 }
 else {
 return LeafNode.__childrenNodeConstructor.EMPTY_NODE;
 }
 };
 /**
 * @inheritDoc
 */
 LeafNode.prototype.hasChild = function () {
 return false;
 };
 /** @inheritDoc */
 LeafNode.prototype.getPredecessorChildName = function (childName, childNode) {
 return null;
 };
 /** @inheritDoc */
 LeafNode.prototype.updateImmediateChild = function (childName, newChildNode) {
 if (childName === '.priority') {
 return this.updatePriority(newChildNode);
 }
 else if (newChildNode.isEmpty() && childName !== '.priority') {
 return this;
 }
 else {
 return LeafNode.__childrenNodeConstructor.EMPTY_NODE
 .updateImmediateChild(childName, newChildNode)
 .updatePriority(this.priorityNode_);
 }
 };
 /** @inheritDoc */
 LeafNode.prototype.updateChild = function (path, newChildNode) {
 var front = path.getFront();
 if (front === null) {
 return newChildNode;
 }
 else if (newChildNode.isEmpty() && front !== '.priority') {
 return this;
 }
 else {
 util_1.assert(front !== '.priority' || path.getLength() === 1, '.priority must be the last token in a path');
 return this.updateImmediateChild(front, LeafNode.__childrenNodeConstructor.EMPTY_NODE.updateChild(path.popFront(), newChildNode));
 }
 };
 /** @inheritDoc */
 LeafNode.prototype.isEmpty = function () {
 return false;
 };
 /** @inheritDoc */
 LeafNode.prototype.numChildren = function () {
 return 0;
 };
 /** @inheritDoc */
 LeafNode.prototype.forEachChild = function (index, action) {
 return false;
 };
 /**
 * @inheritDoc
 */
 LeafNode.prototype.val = function (exportFormat) {
 if (exportFormat && !this.getPriority().isEmpty())
 return {
 '.value': this.getValue(),
 '.priority': this.getPriority().val()
 };
 else
 return this.getValue();
 };
 /** @inheritDoc */
 LeafNode.prototype.hash = function () {
 if (this.lazyHash_ === null) {
 var toHash = '';
 if (!this.priorityNode_.isEmpty())
 toHash +=
 'priority:' +
 snap_1.priorityHashText(this.priorityNode_.val()) +
 ':';
 var type = typeof this.value_;
 toHash += type + ':';
 if (type === 'number') {
 toHash += util_2.doubleToIEEE754String(this.value_);
 }
 else {
 toHash += this.value_;
 }
 this.lazyHash_ = util_2.sha1(toHash);
 }
 return this.lazyHash_;
 };
 /**
 * Returns the value of the leaf node.
 * @return {Object|string|number|boolean} The value of the node.
 */
 LeafNode.prototype.getValue = function () {
 return this.value_;
 };
 /**
 * @inheritDoc
 */
 LeafNode.prototype.compareTo = function (other) {
 if (other === LeafNode.__childrenNodeConstructor.EMPTY_NODE) {
 return 1;
 }
 else if (other instanceof LeafNode.__childrenNodeConstructor) {
 return -1;
 }
 else {
 util_1.assert(other.isLeafNode(), 'Unknown node type');
 return this.compareToLeafNode_(other);
 }
 };
 /**
 * Comparison specifically for two leaf nodes
 * @param {!LeafNode} otherLeaf
 * @return {!number}
 * @private
 */
 LeafNode.prototype.compareToLeafNode_ = function (otherLeaf) {
 var otherLeafType = typeof otherLeaf.value_;
 var thisLeafType = typeof this.value_;
 var otherIndex = LeafNode.VALUE_TYPE_ORDER.indexOf(otherLeafType);
 var thisIndex = LeafNode.VALUE_TYPE_ORDER.indexOf(thisLeafType);
 util_1.assert(otherIndex >= 0, 'Unknown leaf type: ' + otherLeafType);
 util_1.assert(thisIndex >= 0, 'Unknown leaf type: ' + thisLeafType);
 if (otherIndex === thisIndex) {
 // Same type, compare values
 if (thisLeafType === 'object') {
 // Deferred value nodes are all equal, but we should also never get to this point...
 return 0;
 }
 else {
 // Note that this works because true > false, all others are number or string comparisons
 if (this.value_ < otherLeaf.value_) {
 return -1;
 }
 else if (this.value_ === otherLeaf.value_) {
 return 0;
 }
 else {
 return 1;
 }
 }
 }
 else {
 return thisIndex - otherIndex;
 }
 };
 /**
 * @inheritDoc
 */
 LeafNode.prototype.withIndex = function () {
 return this;
 };
 /**
 * @inheritDoc
 */
 LeafNode.prototype.isIndexed = function () {
 return true;
 };
 /**
 * @inheritDoc
 */
 LeafNode.prototype.equals = function (other) {
 /**
 * @inheritDoc
 */
 if (other === this) {
 return true;
 }
 else if (other.isLeafNode()) {
 var otherLeaf = other;
 return (this.value_ === otherLeaf.value_ &&
 this.priorityNode_.equals(otherLeaf.priorityNode_));
 }
 else {
 return false;
 }
 };
 /**
 * The sort order for comparing leaf nodes of different types. If two leaf nodes have
 * the same type, the comparison falls back to their value
 * @type {Array.}
 * @const
 */
 LeafNode.VALUE_TYPE_ORDER = ['object', 'boolean', 'number', 'string'];
 return LeafNode;
}());
exports.LeafNode = LeafNode;

//# sourceMappingURL=LeafNode.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/snap/LeafNode.js
// module id = 15
// module chunks = 0